import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin,
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  Building2,
  AlertCircle,
  FileText,
  Clock,
  User,
  PoundSterling,
  Package,
  Shield,
  ZoomIn,
} from 'lucide-react';

import type { AssetAuditProgress } from '@/types/audit';
import type { Asset } from '@/types/asset';
import { dataService } from '@/services/data-service';

interface AssetDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: AssetAuditProgress;
}

export function AssetDetailsDialog({
  open,
  onOpenChange,
  asset,
}: Readonly<AssetDetailsDialogProps>) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  const [assetDetails, setAssetDetails] = useState<Asset | null>(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);

  // Fetch asset details when dialog opens
  useEffect(() => {
    const fetchAssetDetails = async () => {
      if (!open || !asset.assetCode) return;
      
      setIsLoadingAsset(true);
      try {
        // Fetch assets with search filter to find by code
        const response = await dataService.getAssets({ 
          search: asset.assetCode,
          pageSize: 100 // Get enough results to find the asset
        });
        
        // Find the exact match by code
        const foundAsset = response.data.find((a: Asset) => a.code === asset.assetCode);
        
        if (foundAsset) {
          console.log(`✅ Fetched asset details for ${asset.assetCode}:`, foundAsset);
          setAssetDetails(foundAsset);
        } else {
          console.warn(`⚠️ Asset not found for code ${asset.assetCode}`);
          setAssetDetails(null);
        }
      } catch (error) {
        console.error(`❌ Failed to fetch asset details for ${asset.assetCode}:`, error);
        setAssetDetails(null);
      } finally {
        setIsLoadingAsset(false);
      }
    };

    fetchAssetDetails();
  }, [open, asset.assetCode]);

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(amount);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handlePreviousPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleNextPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex < asset.photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'flagged':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getConditionColor = (condition?: string) => {
    switch (condition) {
      case 'good':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'attention':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col gap-0 bg-white dark:bg-black">
          {/* Fixed Header */}
          <div className="px-6 py-4 border-b bg-white dark:bg-black flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-semibold truncate mb-2">
                  {asset.assetName}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-muted-foreground font-mono">{asset.assetCode}</span>
                  <span className="text-muted-foreground">•</span>
                  <Badge className={getStatusColor(asset.status)}>
                    {asset.status.replace('-', ' ')}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-hidden bg-white dark:bg-black">
            <Tabs defaultValue="audit" className="h-full flex flex-col">
              <div className="flex-shrink-0 border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
                <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
                  <TabsTrigger 
                    value="audit"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Audit Results
                  </TabsTrigger>
                  <TabsTrigger 
                    value="asset"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Asset Details
                  </TabsTrigger>
                  <TabsTrigger 
                    value="photos"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Photos {asset.photos.length > 0 && (
                      <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                        {asset.photos.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="history"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    History
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Audit Results Tab */}
              <TabsContent value="audit" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="space-y-6 py-6">
                  {/* Location Information */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Location Information</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Current Location</span>
                        </div>
                        <span className="text-sm font-medium">{asset.currentLocation}</span>
                      </div>
                      {asset.previousLocation && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Previous Location</span>
                          </div>
                          <span className="text-sm font-medium text-muted-foreground line-through">
                            {asset.previousLocation}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {asset.condition && (
                    <>
                      <Separator />
                      
                      {/* Condition Assessment */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-muted-foreground" />
                          <Label className="text-base font-semibold">Condition Assessment</Label>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Condition Status</span>
                            </div>
                            <Badge className={getConditionColor(asset.condition)}>
                              {asset.condition}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {asset.notes && (
                    <>
                      <Separator />
                      
                      {/* Auditor Notes */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <Label className="text-base font-semibold">Auditor Notes</Label>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed pl-7 whitespace-pre-wrap">
                          {asset.notes}
                        </p>
                      </div>
                    </>
                  )}

                  <Separator />

                  {/* Audit Information */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Audit Information</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Last Updated</span>
                        </div>
                        <span className="text-sm font-medium">
                          {new Date(asset.lastUpdated).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Status</span>
                        </div>
                        <Badge className={getStatusColor(asset.status)}>
                          {asset.status.replace('-', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Asset Details Tab */}
              <TabsContent value="asset" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="space-y-6 py-6">
                  {/* Asset Information */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Asset Information</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      {isLoadingAsset ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Asset Name</p>
                              <p className="text-sm font-medium">{assetDetails?.name ?? asset.assetName}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Asset ID</p>
                              <p className="text-sm font-medium font-mono">{assetDetails?.code ?? asset.assetCode}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Category</p>
                              <p className="text-sm font-medium">{assetDetails?.category ?? asset.category}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Sub Category</p>
                              <p className="text-sm font-medium">{assetDetails?.subCategory ?? '-'}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Status</p>
                              <p className="text-sm font-medium capitalize">{assetDetails?.status ?? 'Active'}</p>
                            </div>
                          </div>
                          {assetDetails?.description && (
                            <div className="space-y-1 pt-2 border-t">
                              <p className="text-xs text-muted-foreground">Description</p>
                              <p className="text-sm font-medium">{assetDetails.description}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Location */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Location</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Current Location</p>
                          <p className="text-sm font-medium">{assetDetails?.location ?? asset.currentLocation}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Sub Location</p>
                          <p className="text-sm font-medium">{assetDetails?.subLocation ?? '-'}</p>
                        </div>
                        {asset.previousLocation && (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Previous Location</p>
                            <p className="text-sm font-medium">{asset.previousLocation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Financial Information */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <PoundSterling className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Financial Information</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Acquisition Cost</p>
                          <p className="text-sm font-medium">{formatCurrency(assetDetails?.acquisitionCost, assetDetails?.currency)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Net Book Value</p>
                          <p className="text-sm font-medium">{formatCurrency(assetDetails?.netBookValue, assetDetails?.currency)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Accumulated Depreciation</p>
                          <p className="text-sm font-medium">{formatCurrency(assetDetails?.accumulatedDepreciation, assetDetails?.currency)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Acquisition Date</p>
                          <p className="text-sm font-medium">{formatDate(assetDetails?.acquisitionDate)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Identification */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Identification</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Serial Number</p>
                          <p className="text-sm font-medium">{assetDetails?.serialNumber ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Manufacturer</p>
                          <p className="text-sm font-medium">{assetDetails?.manufacturer ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Model</p>
                          <p className="text-sm font-medium">{assetDetails?.model ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Barcode</p>
                          <p className="text-sm font-medium font-mono">{assetDetails?.barcode ?? '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Assignment */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Assignment</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Assigned To</p>
                          <p className="text-sm font-medium">{assetDetails?.assignedTo ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Assigned Date</p>
                          <p className="text-sm font-medium">{formatDate(assetDetails?.assignedDate)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Warranty & Insurance */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Warranty & Insurance</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Warranty Provider</p>
                          <p className="text-sm font-medium">{assetDetails?.warrantyProvider ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Warranty Expiry</p>
                          <p className="text-sm font-medium">{formatDate(assetDetails?.warrantyExpiryDate)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Warranty Cost</p>
                          <p className="text-sm font-medium">{formatCurrency(assetDetails?.warrantyCost, assetDetails?.currency)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Warranty Start</p>
                          <p className="text-sm font-medium">{formatDate(assetDetails?.warrantyStartDate)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Insurance Provider</p>
                          <p className="text-sm font-medium">{assetDetails?.insuranceProvider ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Insurance Policy</p>
                          <p className="text-sm font-medium">{assetDetails?.insurancePolicyNumber ?? '-'}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Insurance Value</p>
                          <p className="text-sm font-medium">{formatCurrency(assetDetails?.insuranceValue, assetDetails?.currency)}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Insurance Expiry</p>
                          <p className="text-sm font-medium">{formatDate(assetDetails?.insuranceExpiryDate)}</p>
                        </div>
                      </div>
                      {assetDetails?.warrantyTerms && (
                        <div className="space-y-1 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">Warranty Terms</p>
                          <p className="text-sm font-medium">{assetDetails.warrantyTerms}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Photos Tab */}
              <TabsContent value="photos" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="py-6">
                  {asset.photos.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Camera className="h-5 w-5 text-muted-foreground" />
                          <Label className="text-base font-semibold">Audit Photos</Label>
                          <Badge variant="secondary">{asset.photos.length}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {asset.photos.map((photo, index) => (
                          <button
                            type="button"
                            key={photo.id}
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-80 transition-opacity group text-left"
                            onClick={() => setSelectedPhotoIndex(index)}
                          >
                            {!imageError[photo.id] ? (
                              <>
                                <img
                                  src={photo.thumbnailUrl}
                                  alt={photo.caption ?? `Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={() => setImageError({ ...imageError, [photo.id]: true })}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Camera className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                              <p className="text-white text-xs">
                                {photo.caption ?? `Photo ${index + 1}`}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">No photos attached</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="space-y-6 py-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Activity History</Label>
                    </div>
                    {asset.history && asset.history.length > 0 ? (
                      <div className="space-y-4">
                        {asset.history.map((entry, index) => (
                          <div key={entry.id} className="relative pl-6">
                            <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary" />
                            {index !== (asset.history?.length ?? 0) - 1 && (
                              <div className="absolute left-[3px] top-3 bottom-0 w-px bg-border" />
                            )}
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm font-medium">{entry.description}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(entry.timestamp).toLocaleString()}
                                {entry.user && ` • ${entry.user}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="relative pl-6">
                          <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary" />
                          {(asset.locationStatus === 'changed' || asset.condition) && (
                            <div className="absolute left-[3px] top-3 bottom-0 w-px bg-border" />
                          )}
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm font-medium">Asset audited</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(asset.lastUpdated).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {asset.locationStatus === 'changed' && (
                          <div className="relative pl-6">
                            <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary" />
                            {asset.condition && (
                              <div className="absolute left-[3px] top-3 bottom-0 w-px bg-border" />
                            )}
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm font-medium">Location changed</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                From {asset.previousLocation} to {asset.currentLocation}
                              </p>
                            </div>
                          </div>
                        )}
                        {asset.condition && (
                          <div className="relative pl-6">
                            <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary" />
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm font-medium">Condition assessed</p>
                              <p className="text-xs text-muted-foreground mt-1 capitalize">
                                Condition: {asset.condition}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Full-screen Photo Viewer - Only render when a photo is selected */}
      {selectedPhotoIndex !== null && (
        <dialog
          open
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center w-full h-full max-w-none max-h-none m-0 p-0 border-0"
          aria-label="Photo viewer"
          onClose={() => setSelectedPhotoIndex(null)}
        >
          {/* Backdrop button for closing - accessible click-outside behavior */}
          <button
            type="button"
            className="absolute inset-0 w-full h-full cursor-default bg-transparent"
            onClick={() => setSelectedPhotoIndex(null)}
            aria-label="Close photo viewer"
          />

          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-[10000] text-white hover:bg-white/20 rounded-full"
            onClick={() => setSelectedPhotoIndex(null)}
          >
            <X className="h-6 w-6" />
          </Button>

          {selectedPhotoIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:bg-white/20 rounded-full"
              onClick={handlePreviousPhoto}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
          )}

          <div className="relative z-[10001] max-w-5xl max-h-[90vh] flex flex-col items-center">
            <img
              src={asset.photos[selectedPhotoIndex].url}
              alt={asset.photos[selectedPhotoIndex].caption ?? `Photo ${selectedPhotoIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            
            <div className="mt-4 flex items-center gap-4">
              <p className="text-white/80 text-sm">
                Photo {selectedPhotoIndex + 1} of {asset.photos.length}
              </p>
            </div>
          </div>

          {selectedPhotoIndex < asset.photos.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:bg-white/20 rounded-full"
              onClick={handleNextPhoto}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          )}
        </dialog>
      )}
    </>
  );
}
